// Chauffeur / event "Date + Start + Duration" booking contract.
// The renter picks one date, a start time and a duration; the end date/time
// for the quote and the trip record is derived here using the same local
// wall-clock arithmetic as the server's pricing and stored trip clocks
// (independent of browser timezone; a DST night does not add or remove a
// billed hour). Physical-elapsed DST charging would need a backend contract.
import { parseBookingTime } from "./booking-time";

export const FULL_DAY_HOURS = 24;
export const DEFAULT_HOURLY_START = "12:00 pm";
export const DEFAULT_HOURLY_DURATION = 2;
const TWELVE_HOUR_MARKETS = new Set(["uk", "gb", "us", "ca", "au", "nz", "ie"]);

/** 24-hour clock everywhere except UK, US, CA, AU, NZ and IE (and the worldwide English site). */
export function usesTwelveHourClock(market: string | null | undefined, language?: string): boolean {
  if (market) return TWELVE_HOUR_MARKETS.has(market.toLowerCase());
  return !language || language.toLowerCase().startsWith("en");
}

/**
 * Minimum hours through 12, then Full day (24h). A minimum above 12h is not
 * clamped down: only Full day is offered for 13..24, nothing beyond 24.
 */
export function hourlyDurationOptions(minHours = 1): number[] {
  const min = Math.max(1, Math.ceil(Number.isFinite(minHours) ? minHours : 1));
  if (min > FULL_DAY_HOURS) return [];
  const out: number[] = [];
  for (let h = min; h <= 12; h++) out.push(h);
  out.push(FULL_DAY_HOURS);
  return out;
}

/**
 * Minimum bookable hours from the car's own data: an explicit minimum
 * duration when provided, else the minimum charge divided by the hourly rate
 * (e.g. 340 floor / 170 per hour = 2h). Defaults to 1h.
 */
export function minimumHoursFor(input: { explicitMinHours?: number | null; minimumCharge?: number | null; hourlyRate?: number | null }): number {
  const explicit = Number(input.explicitMinHours);
  if (Number.isFinite(explicit) && explicit > 0) return Math.ceil(explicit);
  const floor = Number(input.minimumCharge), rate = Number(input.hourlyRate);
  if (Number.isFinite(floor) && floor > 0 && Number.isFinite(rate) && rate > 0) return Math.max(1, Math.ceil(floor / rate - 1e-9));
  return 1;
}

export function to12h(hour24: number, minute: number): string {
  return `${hour24 % 12 || 12}:${String(minute).padStart(2, "0")} ${hour24 >= 12 ? "pm" : "am"}`;
}

/** Start options at 15-minute steps, value in the widget's "h:mm am" contract. */
export function quarterHourStartOptions(twelveHour: boolean, current?: string): Array<{ value: string; label: string }> {
  const opts: Array<{ value: string; label: string; minutes: number }> = [];
  for (let m = 0; m < 24 * 60; m += 15) {
    const h = Math.floor(m / 60), mm = m % 60;
    opts.push({ value: to12h(h, mm), label: formatClock(h, mm, twelveHour), minutes: m });
  }
  const parsed = current ? parseBookingTime(current) : null;
  if (parsed && parsed.minute % 15 !== 0) {
    const minutes = parsed.hour24 * 60 + parsed.minute;
    opts.push({ value: to12h(parsed.hour24, parsed.minute), label: formatClock(parsed.hour24, parsed.minute, twelveHour), minutes });
    opts.sort((a, b) => a.minutes - b.minutes);
  }
  return opts.map(({ value, label }) => ({ value, label }));
}

export function formatClock(hour24: number, minute: number, twelveHour: boolean): string {
  const mm = String(minute).padStart(2, "0");
  return twelveHour ? `${hour24 % 12 || 12}:${mm} ${hour24 >= 12 ? "PM" : "AM"}` : `${String(hour24).padStart(2, "0")}:${mm}`;
}

/** Selected date + wall clock -> minutes on a naive (timezone-free) axis. */
function wallMinutes(date: string, hour24: number, minute: number): number {
  const [y, mo, d] = date.split("-").map(Number);
  return Date.UTC(y, mo - 1, d, hour24, minute) / 60000;
}

function fromWallMinutes(total: number) {
  const d = new Date(total * 60000);
  return { date: d.toISOString().slice(0, 10), hour24: d.getUTCHours(), minute: d.getUTCMinutes() };
}

/** Start + duration -> derived end (date + "h:mm am"). Null when inputs are invalid. */
export function deriveHourlyEnd(date: string, startTime: string, hours: number): { dateTo: string; dropoffTime: string } | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(date) || !(hours > 0)) return null;
  const start = parseBookingTime(startTime);
  if (!start) return null;
  const end = fromWallMinutes(wallMinutes(date, start.hour24, start.minute) + Math.round(hours * 60));
  return { dateTo: end.date, dropoffTime: to12h(end.hour24, end.minute) };
}

/** Restore a duration from a previously saved interval; null when not representable. */
export function elapsedHours(dateFrom: string, pickup: string, dateTo: string, dropoff: string): number | null {
  const a = parseBookingTime(pickup), b = parseBookingTime(dropoff);
  if (!a || !b || !/^\d{4}-\d{2}-\d{2}$/.test(dateFrom) || !/^\d{4}-\d{2}-\d{2}$/.test(dateTo || "")) return null;
  const h = (wallMinutes(dateTo, b.hour24, b.minute) - wallMinutes(dateFrom, a.hour24, a.minute)) / 60;
  return h > 0 ? h : null;
}

export function pickRestoredDuration(options: number[], hours: number | null): number {
  if (options.length === 0) return FULL_DAY_HOURS;
  if (hours != null) {
    const exact = options.find(o => Math.abs(o - hours) < 0.01);
    if (exact) return exact;
  }
  return options.includes(DEFAULT_HOURLY_DURATION) ? DEFAULT_HOURLY_DURATION : options[0];
}

/**
 * Checkout guard for a duration-based chauffeur interval. A next-day end is
 * only honoured when it is the coherent start + explicit hours (<= 24h, exact
 * local wall-clock elapsed hours). Anything else keeps the legacy
 * single-day lock so hand-edited multi-day URLs can never be quoted.
 */
export function coherentChauffeurDateTo(dateFrom: string, rawDateTo: string, pickup?: string | null, dropoff?: string | null, hours?: number | null): string {
  if (!dateFrom) return rawDateTo;
  if (!rawDateTo || rawDateTo === dateFrom || !pickup || !dropoff || !(hours != null && hours > 0 && hours <= FULL_DAY_HOURS)) return dateFrom;
  const naiveNext = deriveHourlyEnd(dateFrom, "12:00 am", 24)?.dateTo;
  if (rawDateTo !== naiveNext) return dateFrom;
  const elapsed = elapsedHours(dateFrom, pickup, rawDateTo, dropoff);
  return elapsed != null && elapsed <= FULL_DAY_HOURS && Math.abs(elapsed - hours) < 1e-6 ? rawDateTo : dateFrom;
}
